import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { projectsApi } from '../../services/api'
import { showSuccess, showError } from './appSlice'

const initialState = {
  projects: [],
  currentProject: null,
  loading: false,
  error: null,
  filters: {
    technology: '',
    category: '',
    search: '',
  },
  pagination: {
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  },
}

// Async thunks
export const fetchProjects = createAsyncThunk(
  'portfolio/fetchProjects',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const { filters, pagination } = getState().portfolio
      const queryParams = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        ...params,
      }
      
      const response = await projectsApi.getAll(queryParams)
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки проектов')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchProjectById = createAsyncThunk(
  'portfolio/fetchProjectById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await projectsApi.getById(id)
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Проект не найден')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createProject = createAsyncThunk(
  'portfolio/createProject',
  async (projectData, { dispatch, rejectWithValue }) => {
    try {
      const response = await projectsApi.create(projectData)
      
      if (response.success) {
        dispatch(showSuccess('Проект создан успешно'))
        dispatch(fetchProjects())
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка создания проекта')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при создании проекта'))
      return rejectWithValue(error.message)
    }
  }
)

export const updateProject = createAsyncThunk(
  'portfolio/updateProject',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await projectsApi.update(id, data)
      
      if (response.success) {
        dispatch(showSuccess('Проект обновлен успешно'))
        dispatch(fetchProjects())
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления проекта')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении проекта'))
      return rejectWithValue(error.message)
    }
  }
)

export const deleteProject = createAsyncThunk(
  'portfolio/deleteProject',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await projectsApi.delete(id)
      
      if (response.success) {
        dispatch(showSuccess('Проект удален успешно'))
        dispatch(fetchProjects())
        return id
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления проекта')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при удалении проекта'))
      return rejectWithValue(error.message)
    }
  }
)

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      const { key, value } = action.payload
      state.filters[key] = value
      state.pagination.page = 1 // Reset to first page when filtering
    },
    
    clearFilters: (state) => {
      state.filters = {
        technology: '',
        category: '',
        search: '',
      }
      state.pagination.page = 1
    },
    
    setPage: (state, action) => {
      state.pagination.page = action.payload
    },
    
    setCurrentProject: (state, action) => {
      state.currentProject = action.payload
    },
    
    clearCurrentProject: (state) => {
      state.currentProject = null
    },
    
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch Projects
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false
        state.projects = action.payload.projects || action.payload || []
        state.pagination = {
          ...state.pagination,
          ...action.payload.pagination,
        }
        state.error = null
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
    
    // Fetch Project By ID
    builder
      .addCase(fetchProjectById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.loading = false
        state.currentProject = action.payload
        state.error = null
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
    
    // Create Project
    builder
      .addCase(createProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createProject.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
    
    // Update Project
    builder
      .addCase(updateProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProject.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
    
    // Delete Project
    builder
      .addCase(deleteProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteProject.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const {
  setFilter,
  clearFilters,
  setPage,
  setCurrentProject,
  clearCurrentProject,
  clearError,
} = portfolioSlice.actions

// Selectors
export const selectFilteredProjects = (state) => {
  const { projects, filters } = state.portfolio
  
  return projects.filter(project => {
    const matchesTechnology = !filters.technology || 
      project.technologies?.some(tech => 
        tech.toLowerCase().includes(filters.technology.toLowerCase())
      )
    
    const matchesCategory = !filters.category || 
      project.category?.toLowerCase() === filters.category.toLowerCase()
    
    const matchesSearch = !filters.search ||
      project.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
      project.description?.toLowerCase().includes(filters.search.toLowerCase())
    
    return matchesTechnology && matchesCategory && matchesSearch
  })
}

export const selectProjectTechnologies = (state) => {
  const { projects } = state.portfolio
  const technologies = new Set()
  
  projects.forEach(project => {
    project.technologies?.forEach(tech => technologies.add(tech))
  })
  
  return Array.from(technologies).sort()
}

export const selectProjectCategories = (state) => {
  const { projects } = state.portfolio
  const categories = new Set()
  
  projects.forEach(project => {
    if (project.category) categories.add(project.category)
  })
  
  return Array.from(categories).sort()
}

export default portfolioSlice.reducer